/**
 * Phone Numbers Tab — Account Detail
 *
 * Per-tenant Telnyx provisioning surface for super admins. Two carrier
 * registration routes share the same chassis:
 *   - 10DLC route: Brand → Campaign → Numbers
 *   - Toll-Free route: Number → Toll-Free Verification (TFV)
 *
 * The active route is seeded by the latest open `phone_number_request` ticket
 * the customer raised during OOBE, then the operator can switch.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@respectly/ui';
import { Phone, AlertCircle, Wifi, WifiOff, Ticket } from 'lucide-react';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import { useTelnyxStore } from '../../../stores/useTelnyxStore';
import {
  ProvisioningPipeline,
  type CarrierRoute,
} from '../phone-numbers/ProvisioningPipeline';
import { NumberList } from '../phone-numbers/NumberList';
import { ProvisionNumberDialog } from '../phone-numbers/ProvisionNumberDialog';
import { TfvSubmissionDialog } from '../phone-numbers/TfvSubmissionDialog';
import { AssignExistingNumberDialog } from '../phone-numbers/AssignExistingNumberDialog';

interface PhoneNumbersTabProps {
  accountId: string;
}

interface PhoneNumberRequestSummary {
  id: string;
  requestedType: CarrierRoute;
  createdAt: string;
  status: string;
  description: string;
}

interface SupportRequestApiItem {
  id: string;
  category: string;
  status: string;
  createdAt: string;
  description?: string | null;
}

/**
 * Parse the OOBE-generated description to extract the requested type.
 * The description is generated by CommunicationsStep.tsx and looks like:
 *   "...Number type: Toll-Free..."  or  "...Number type: Local 10DLC..."
 */
function parseRequestedType(description: string | null | undefined): CarrierRoute | null {
  if (!description) return null;
  const m = description.match(/Number type:\s*([^\r\n]+)/i);
  if (!m || !m[1]) return null;
  const value = m[1].trim().toLowerCase();
  if (value.startsWith('toll')) return 'tollfree';
  if (value.startsWith('local')) return '10dlc';
  return null;
}

export function PhoneNumbersTab({ accountId }: PhoneNumbersTabProps) {
  const {
    statusByAccount,
    loadingStatusByAccount,
    errorByAccount,
    isCreatingBrand,
    isRefreshingBrand,
    isCreatingCampaign,
    isRefreshingCampaign,
    isSubmittingTfv,
    isRefreshingTfv,
    isProvisioningNumber,
    isSearching,
    searchResults,
    searchError,
    loadStatus,
    registerBrand,
    refreshBrand,
    createCampaign,
    refreshCampaign,
    submitTfv,
    refreshTfv,
    searchNumbers,
    provisionNumber,
    updateNumber,
    releaseNumber,
    clearSearch,
  } = useTelnyxStore();

  const status = statusByAccount[accountId];
  const isLoading = loadingStatusByAccount[accountId];
  const error = errorByAccount[accountId];

  const [provisionDialogOpen, setProvisionDialogOpen] = useState(false);
  const [tfvDialogOpen, setTfvDialogOpen] = useState(false);
  const [assignExistingOpen, setAssignExistingOpen] = useState(false);
  const [route, setRoute] = useState<CarrierRoute>('10dlc');
  const [routeUserOverridden, setRouteUserOverridden] = useState(false);
  const [latestRequest, setLatestRequest] = useState<PhoneNumberRequestSummary | null>(null);

  useEffect(() => {
    loadStatus(accountId);
  }, [accountId, loadStatus]);

  // Fetch the most recent open phone_number_request support ticket for this
  // tenant so we can seed the route + show a banner. Best-effort — failure here
  // does not block the rest of the tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = getApiClient();
        const url = `${API_ENDPOINTS.SUPER_ADMIN.SUPPORT.REQUESTS}?tenantId=${encodeURIComponent(accountId)}&category=phone_number_request&limit=5&sortBy=createdAt&sortOrder=desc`;
        const result = await api.get<{
          requests?: SupportRequestApiItem[];
          data?: SupportRequestApiItem[];
        }>(url);
        const items: SupportRequestApiItem[] = Array.isArray(result?.requests)
          ? result.requests
          : Array.isArray(result?.data)
            ? result.data
            : [];
        const open = items.find(
          (r) => r.status === 'open' || r.status === 'in_progress',
        );
        const pick = open || items[0] || null;
        if (!pick) {
          if (!cancelled) setLatestRequest(null);
          return;
        }
        const requestedType = parseRequestedType(pick.description ?? '') ?? '10dlc';
        if (cancelled) return;
        setLatestRequest({
          id: pick.id,
          requestedType,
          createdAt: pick.createdAt,
          status: pick.status,
          description: pick.description ?? '',
        });
      } catch {
        // Non-fatal — tab still works without ticket context.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Seed route from the ticket once, unless the operator has overridden.
  useEffect(() => {
    if (!routeUserOverridden && latestRequest) {
      setRoute(latestRequest.requestedType);
    }
  }, [latestRequest, routeUserOverridden]);

  const campaignReady = useMemo(
    () => status?.campaign?.status?.toUpperCase() === 'APPROVED',
    [status],
  );

  if (isLoading && !status) return <LoadingSkeleton />;

  if (error && !status) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Couldn&apos;t load Telnyx status</p>
            <p className="mt-1 text-xs">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const openProvisionDialog = () => {
    clearSearch();
    setProvisionDialogOpen(true);
  };

  const handleChangeRoute = (next: CarrierRoute) => {
    setRoute(next);
    setRouteUserOverridden(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="space-y-5"
    >
      {/* Connection header */}
      <ConnectionHeader status={status} />

      {/* Customer-requested route banner */}
      {latestRequest && (
        <RequestedRouteBanner
          request={latestRequest}
          activeRoute={route}
          onApply={() => handleChangeRoute(latestRequest.requestedType)}
        />
      )}

      {/* Provisioning pipeline */}
      <ProvisioningPipeline
        route={route}
        onChangeRoute={handleChangeRoute}
        status={status}
        isCreatingBrand={isCreatingBrand}
        isRefreshingBrand={isRefreshingBrand}
        isCreatingCampaign={isCreatingCampaign}
        isRefreshingCampaign={isRefreshingCampaign}
        onRegisterBrand={() => registerBrand(accountId)}
        onRefreshBrand={() => refreshBrand(accountId)}
        onCreateCampaign={() => createCampaign(accountId)}
        onRefreshCampaign={() => refreshCampaign(accountId)}
        isRefreshingTfv={isRefreshingTfv}
        onSubmitTfv={() => setTfvDialogOpen(true)}
        onRefreshTfv={() => refreshTfv(accountId)}
        onProvisionNumber={openProvisionDialog}
      />

      {/* Number list */}
      <NumberList
        numbers={status.numbers}
        campaignReady={campaignReady}
        onProvisionNumber={openProvisionDialog}
        onAssignExisting={() => setAssignExistingOpen(true)}
        onSetDefault={(phoneNumberId) =>
          updateNumber(accountId, phoneNumberId, { isDefault: true })
        }
        onRename={(phoneNumberId, friendlyName) =>
          updateNumber(accountId, phoneNumberId, { friendlyName })
        }
        onRelease={(phoneNumberId) => releaseNumber(accountId, phoneNumberId)}
      />

      {/* Provision dialog */}
      <ProvisionNumberDialog
        open={provisionDialogOpen}
        onClose={() => setProvisionDialogOpen(false)}
        onSearch={(criteria) => searchNumbers(accountId, criteria)}
        onProvision={(params) => provisionNumber(accountId, params)}
        searchResults={searchResults}
        isSearching={isSearching}
        isProvisioning={isProvisioningNumber}
        searchError={searchError}
        campaignReady={campaignReady}
        defaultType={route === 'tollfree' ? 'tollfree' : 'local'}
        hideCampaignWarning={route === 'tollfree'}
      />

      {/* Toll-Free Verification submission dialog */}
      <TfvSubmissionDialog
        open={tfvDialogOpen}
        onClose={() => setTfvDialogOpen(false)}
        status={status}
        isSubmitting={isSubmittingTfv}
        onSubmit={(params) => submitTfv(accountId, params)}
      />

      {/* Assign Existing Number dialog */}
      <AssignExistingNumberDialog
        open={assignExistingOpen}
        onClose={() => setAssignExistingOpen(false)}
        tenantId={accountId}
        onAssigned={() => loadStatus(accountId)}
      />
    </motion.div>
  );
}

// ─── Connection Header ───────────────────────────────────────────────────────

function ConnectionHeader({
  status,
}: {
  status: { tenantConfigured: boolean; businessIdentityComplete: boolean };
}) {
  const ok = status.tenantConfigured;
  const Icon = ok ? Wifi : WifiOff;

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-xl border px-4 py-3 text-sm',
        ok
          ? 'border-emerald-200 bg-emerald-50/60 text-emerald-900 dark:border-emerald-800/40 dark:bg-emerald-950/30 dark:text-emerald-200'
          : 'border-amber-200 bg-amber-50/60 text-amber-900 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-200',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md ring-1',
            ok
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/50'
              : 'bg-amber-100 text-amber-700 ring-amber-200/70 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800/50',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
            Telnyx connection
          </p>
          <p className="text-sm font-medium">
            {ok
              ? 'API key resolved · webhooks routed to api.respectly.ai'
              : 'API key not configured — set TELNYX_API_KEY or AZURE_KEY_VAULT_URL'}
          </p>
        </div>
      </div>
      {!status.businessIdentityComplete && (
        <div className="hidden items-center gap-1.5 text-xs sm:flex">
          <AlertCircle className="h-3.5 w-3.5" />
          <span>Tenant business identity is incomplete</span>
        </div>
      )}
    </div>
  );
}

// ─── Requested-route banner ─────────────────────────────────────────────────

function RequestedRouteBanner({
  request,
  activeRoute,
  onApply,
}: {
  request: PhoneNumberRequestSummary;
  activeRoute: CarrierRoute;
  onApply: () => void;
}) {
  const matches = activeRoute === request.requestedType;
  const requestedLabel = request.requestedType === 'tollfree' ? 'Toll-Free Number' : 'Local 10DLC Number';
  const createdAt = new Date(request.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
        matches
          ? 'border-sky-200 bg-sky-50/60 text-sky-900 dark:border-sky-800/40 dark:bg-sky-950/30 dark:text-sky-200'
          : 'border-violet-200 bg-violet-50/60 text-violet-900 dark:border-violet-800/40 dark:bg-violet-950/30 dark:text-violet-200',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md ring-1',
            matches
              ? 'bg-sky-100 text-sky-700 ring-sky-200/70 dark:bg-sky-900/40 dark:text-sky-300 dark:ring-sky-800/50'
              : 'bg-violet-100 text-violet-700 ring-violet-200/70 dark:bg-violet-900/40 dark:text-violet-300 dark:ring-violet-800/50',
          )}
        >
          <Ticket className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
            Customer request · {createdAt}
          </p>
          <p className="text-sm font-medium">
            Customer requested a <span className="font-semibold">{requestedLabel}</span>{' '}
            <span className="font-mono text-[11px] opacity-70">· ticket {request.id.slice(0, 8)}</span>
          </p>
        </div>
      </div>
      {!matches && (
        <button
          type="button"
          onClick={onApply}
          className="rounded-md bg-white/80 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-800 ring-1 ring-violet-200 transition-colors hover:bg-white dark:bg-slate-900/60 dark:text-violet-200 dark:ring-violet-800/60 dark:hover:bg-slate-900"
        >
          Switch to {request.requestedType === 'tollfree' ? 'Toll-Free' : '10DLC'} route
        </button>
      )}
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      <div className="h-14 rounded-xl bg-slate-100 dark:bg-slate-800" />
      <div className="h-[440px] rounded-2xl bg-slate-100 dark:bg-slate-800" />
      <div className="h-40 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      <div className="sr-only">
        <Phone /> Loading phone numbers...
      </div>
    </div>
  );
}
